
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { decodeCustomQuestion } from '@/utils/decoding';
import CustomQuestions from './CustomQuestions.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import SelectComponent from '@/components/shared/Select.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';

const namespace: string = 'customQuestions';

@Component<CustomFields>({
	components: {
		CustomQuestions,
		InfoBanner,
		SelectComponent,
		TextButton
	}
})
export default class CustomFields extends Vue {
	@Action('setCustomFields', { namespace }) private setCustomFields!: (fields: CustomQuestion[]) => void;
	@Action('addCustomField', { namespace }) private addCustomField!: (field: CustomQuestion) => void;
	@Action('setCustomFieldsInitialized', { namespace }) private setCustomFieldsInitialized!: (initialized: boolean) => void;
	@Getter('getAllQuestions', { namespace }) private allQuestions!: CustomQuestion[];
	@Getter('getCustomFields', { namespace }) private customFields!: CustomQuestion[];
	@Getter('getCustomFieldsInitialized', { namespace }) private customFieldsInitialized!: boolean;
	@Getter('getCheckoutAnsweredQuestions', { namespace: 'orders' }) private checkoutAnsweredQuestions!: AnsweredCustomQuestion[] | null;
	@Getter('getOrderDetails', { namespace: 'orders' }) private orderDetails!: OrderDetails;
	@Getter('getRepeatOrderNotices', { namespace: 'orders' }) private repeatOrderNotices!: RepeatOrderNotices | null;
	@Prop({ type: Boolean, default: false }) private hideHeader!: boolean;
	@Prop({ type: Array, required: false }) private defaultQuestions!: CustomQuestion[];

	$refs!: {
		fieldSelect: Vue;
	};

	private selectedQuestion: CustomQuestion | null = null;
	private selectMenuMaxWidth: number | string = 'auto';

	/**
	 * Show the pre-filled notice when repeating a catering event order that had custom questions answered.
	 * Other orders show it in the special requests section that holds these fields.
	 *
	 * @return {boolean}
	 */
	private get showPrefilledNotice(): boolean {
		return !!this.repeatOrderNotices && this.orderDetails.takeoutType === 'catering-event' && !!this.checkoutAnsweredQuestions?.length;
	}

	/**
	 * Get the list of questions that haven't been added yet.
	 *
	 * @return {CustomQuestion[]}
	 */
	private get availableQuestions(): CustomQuestion[] {
		return this.allQuestions.filter((q) => !this.customFields.some((added) => added.question === q.question));
	}

	/**
	 * Restore previously added questions from the checkout answered questions in the store,
	 * then add any default questions that haven't been added/answered yet.
	 *
	 * @return {void}
	 */
	private created(): void {
		if (!this.customFieldsInitialized) {
			let customFields: CustomQuestion[] = [];
			if (this.checkoutAnsweredQuestions?.length) {
				customFields = this.checkoutAnsweredQuestions
					.map((answered) => this.allQuestions.find((q) => decodeCustomQuestion(q).question === answered.question))
					.filter((q): q is CustomQuestion => !!q);
			}
			if (this.defaultQuestions?.length) {
				this.defaultQuestions.forEach((q: CustomQuestion) => {
					if (!customFields.some((added) => added.question === q.question)) {
						customFields.push(q);
					}
				});
			}
			this.setCustomFields(customFields);
			this.setCustomFieldsInitialized(true);
		}
	}

	/**
	 * Cap the dropdown menu width so it can grow past the select for long questions but never past the modal's content edge.
	 *
	 * @return {void}
	 */
	private setSelectMenuMaxWidth(): void {
		const select = this.$refs.fieldSelect?.$el?.getBoundingClientRect();
		if (!select) {
			return;
		}
		const modalContent = this.$el.closest('.inner-modal-wrapper');
		const rightEdge = modalContent ? modalContent.getBoundingClientRect().right : window.innerWidth;
		this.selectMenuMaxWidth = Math.max(rightEdge - select.left, select.width);
	}

	/**
	 * Format the question label, prefixing with [Internal] if applicable.
	 *
	 * @param {CustomQuestion} question
	 * @return {string}
	 */
	private formatQuestionLabel(question: CustomQuestion): string {
		const label = this.$options.filters?.localize(question, 'question') || question.question;
		return question.is_internal ? `[${this.$t('settings.custom_questions.is_internal')}] ${label}` : label;
	}

	/**
	 * Add the selected question to the custom fields store
	 *
	 * @return {void}
	 */
	private addField(): void {
		if (this.selectedQuestion) {
			this.addCustomField(this.selectedQuestion);
			this.selectedQuestion = null;
		}
	}
}
